import type { ControlPlaneOperationalProjection } from '@internal/spassu-code-contract';
import { Box, Chip, Typography, makeStyles } from '@material-ui/core';
import {
  ATTEMPT_STATE_LABELS,
  ACTIVITY_CYCLE_LABELS,
  BOOTSTRAP_ACTION_LABELS,
  BOOTSTRAP_STAGE_LABELS,
  CONNECTION_LABELS,
  EXPIRY_BAND_LABELS,
  SIGNAL_HEALTH_LABELS,
  WORKSPACE_LABELS,
  formatManagementInstant,
} from './control-plane-management-labels';
import { ControlPlaneSessionObservabilityPanel } from './ControlPlaneSessionObservabilityPanel';

const useStyles = makeStyles(theme => ({
  grid: {
    display: 'grid',
    gap: theme.spacing(2),
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  },
  dimension: {
    display: 'grid',
    gap: theme.spacing(0.5),
  },
  reason: {
    color: theme.palette.text.secondary,
  },
}));

export { CapabilityButton } from './ControlPlaneCapabilityButton';
export {
  ACTIVITY_CYCLE_LABELS,
  CONNECTION_LABELS,
  EXPIRY_BAND_LABELS,
  WORKSPACE_LABELS,
} from './control-plane-management-labels';

const FINALIZATION_LABELS: Record<string, string> = {
  'not-started': 'Não iniciada',
  pending: 'Aguardando confirmação',
  completed: 'Concluída',
  aborted: 'Abortada',
  unavailable: 'Indisponível',
};

const TELEMETRY_LABELS: Record<string, string> = {
  'not-started': 'Sem espera de telemetria',
  'awaiting-stop': 'Aguardando evento final do Claude',
  flushing: 'Entregando métricas',
  confirmed: 'Métricas confirmadas',
  'timed-out': 'Concluída com métricas pendentes',
  unavailable: 'Estado de telemetria indisponível',
};

const RECOVERY_LABELS: Record<string, string> = {
  'not-started': 'Não iniciada',
  created: 'Código emitido',
  claimed: 'Leitura local autorizada',
  completed: 'Reconciliada',
  failed: 'Falhou',
  expired: 'Expirada',
  unavailable: 'Indisponível',
};

const COLLECTOR_LABELS: Record<string, string> = {
  compatible: 'Compatível',
  outdated: 'Desatualizado',
  pending: 'Entrega pendente',
  failed: 'Falhou',
  unavailable: 'Indisponível',
};

function StateDimension(props: {
  label: string;
  value: string;
  detail?: string | null;
}) {
  const classes = useStyles();
  return (
    <Box className={classes.dimension}>
      <Typography variant="overline" component="h3">
        {props.label}
      </Typography>
      <Typography variant="body1">{props.value}</Typography>
      {props.detail ? (
        <Typography variant="caption" className={classes.reason}>
          {props.detail}
        </Typography>
      ) : null}
    </Box>
  );
}

export function ManagementStatePanel(props: {
  projection: ControlPlaneOperationalProjection;
  stale: boolean;
}) {
  const classes = useStyles();
  return (
    <Box className={classes.grid} data-testid="management-state">
      <CoreStateDimensions projection={props.projection} />
      <AttemptDimension projection={props.projection} />
      <UsageReliabilityDimensions projection={props.projection} />
      <ControlPlaneSessionObservabilityPanel
        projection={props.projection}
        stale={props.stale}
      />
      {props.stale ? (
        <Chip
          size="small"
          label="Leitura desatualizada"
          role="status"
          aria-label="Leitura desatualizada; mostrando o último estado conhecido"
        />
      ) : null}
    </Box>
  );
}

function AttemptDimension(props: {
  projection: ControlPlaneOperationalProjection;
}) {
  const classes = useStyles();
  const attempt = props.projection.attempt;
  if (!attempt) {
    return (
      <StateDimension
        label="Tentativa de conexão"
        value="Nenhuma tentativa registrada"
      />
    );
  }
  const detail = attemptDetail(attempt);
  const content = (
    <Box className={classes.dimension}>
      <Typography variant="overline" component="h3">
        Tentativa de conexão
      </Typography>
      <Typography variant="body1">
        {ATTEMPT_STATE_LABELS[attempt.state]}
      </Typography>
      <Typography variant="body2">
        {BOOTSTRAP_STAGE_LABELS[attempt.stage]}
      </Typography>
      <Typography variant="caption" className={classes.reason}>
        {detail}
      </Typography>
      {attempt.purpose === 'renewal' && attempt.state === 'failed' ? (
        <Typography variant="caption">
          A conexão anterior permanece disponível enquanto ainda estiver válida.
        </Typography>
      ) : null}
    </Box>
  );
  return attempt.state === 'failed' ? (
    <Box role="alert">{content}</Box>
  ) : (
    <Box aria-live="polite" aria-atomic="true">
      {content}
    </Box>
  );
}

function attemptDetail(
  attempt: NonNullable<ControlPlaneOperationalProjection['attempt']>,
): string {
  if (attempt.diagnostic) {
    return `${attempt.diagnostic.code} · ${
      BOOTSTRAP_ACTION_LABELS[attempt.diagnostic.actionCode]
    }`;
  }
  if (attempt.state === 'failed') {
    return 'A tentativa não foi concluída. Entre em contato com o suporte.';
  }
  return `Última mudança: ${formatManagementInstant(attempt.stageChangedAt)}`;
}

function CoreStateDimensions(props: {
  projection: ControlPlaneOperationalProjection;
}) {
  const { projection } = props;
  return (
    <>
      <StateDimension
        label="Ciclo da atividade"
        value={ACTIVITY_CYCLE_LABELS[projection.activityCycle]}
      />
      <StateDimension
        label="Conexão"
        value={CONNECTION_LABELS[projection.connection.state]}
        detail={EXPIRY_BAND_LABELS[projection.connection.expiryBand]}
      />
      <StateDimension
        label="Validade"
        value={
          formatManagementInstant(projection.connection.expiresAt) ??
          'Não aplicável'
        }
        detail={
          projection.connection.lastConfirmedAt
            ? `Última confirmação: ${formatManagementInstant(
                projection.connection.lastConfirmedAt,
              )}`
            : null
        }
      />
      <StateDimension
        label="Workspace local"
        value={WORKSPACE_LABELS[projection.workspace.state]}
        detail={
          projection.workspace.nextAttemptAt
            ? `Próxima tentativa: ${formatManagementInstant(
                projection.workspace.nextAttemptAt,
              )}`
            : projection.workspace.lastErrorCode
        }
      />
    </>
  );
}

function UsageReliabilityDimensions(props: {
  projection: ControlPlaneOperationalProjection;
}) {
  const { projection } = props;
  return (
    <>
      <StateDimension
        label="Finalização das métricas"
        value={
          FINALIZATION_LABELS[projection.finalization?.state ?? 'unavailable']
        }
        detail={
          TELEMETRY_LABELS[
            projection.finalization?.telemetryState ?? 'unavailable'
          ]
        }
      />
      <StateDimension
        label="Recuperação de tokens"
        value={
          RECOVERY_LABELS[projection.usageRecovery?.state ?? 'unavailable']
        }
        detail={recoveryDetail(projection)}
      />
      <StateDimension
        label="Coletor Claude"
        value={
          COLLECTOR_LABELS[projection.usageCollector?.state ?? 'unavailable']
        }
        detail={collectorDetail(projection)}
      />
      <StateDimension
        label="Integridade sessão e origem"
        value={
          SIGNAL_HEALTH_LABELS[
            projection.signalHealth?.condition ?? 'not_observed'
          ]
        }
        detail={projection.signalHealth?.reasonCode ?? null}
      />
    </>
  );
}

function recoveryDetail(
  projection: ControlPlaneOperationalProjection,
): string | null {
  const recovery = projection.usageRecovery;
  if (!recovery) return 'Projeção legada sem estado de recuperação.';
  const counts = recovery.counts;
  if (recovery.state === 'completed') {
    return `${counts.found} encontrados; ${counts.accepted} aceitos; ${counts.replayed} reapresentados; ${counts.excluded} excluídos; ${counts.unrecoverable} não recuperáveis.`;
  }
  return recovery.lastErrorCode ?? null;
}

function collectorDetail(
  projection: ControlPlaneOperationalProjection,
): string | null {
  const collector = projection.usageCollector;
  if (!collector) return 'Projeção legada sem saúde do coletor.';
  const versions = [collector.installedVersion, collector.expectedVersion]
    .filter(Boolean)
    .join(' / ');
  const pending = `${collector.pendingCount} pendente(s)`;
  return [versions || null, pending, collector.lastErrorCode]
    .filter(Boolean)
    .join(' · ');
}
